import { useState, useRef } from 'react';
import { MessageSquare, Bell, Menu } from 'react-feather';
import { useNavigate, Link } from 'react-router-dom';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { useAuth } from '../hooks/useAuth';
import MessagesTray from './MessagesTray';
import NotificationTray from './NotificationTray';
import TrialCountdownChip from './remember/TrialCountdownChip';
import { trialCountdownOf } from '../services/trialService';

function F8Sparkle() {
  return (
    <span className="f8-sparkle-nav">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" fill="#fff"/>
      </svg>
    </span>
  );
}

export default function WebTopBar({ onToggleRail, title, subtitle, trailing }) {
  const { user } = useAuth();
  const unreadMessages = useUnreadMessages(user?.uid);
  // Bell badge count (PPL-157): reuse NotificationTray's existing subscription —
  // it's permanently mounted here and already computes the unread count — rather
  // than opening a second identical Firestore listener. The tray keeps its last
  // known count on a transient listener error, so the badge won't blank out.
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const navigate = useNavigate();
  const [msgTrayOpen, setMsgTrayOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const msgBtnRef = useRef(null);
  const bellRef = useRef(null);

  const isPremium = user?.selectedPlan === 'premium';
  // PPL-358 — a trialist IS on premium; the countdown replaces the cold upsell.
  const countdown = trialCountdownOf(user);

  return (
    <div className="web-top-bar">
      <div className="top-bar-left">
        <button className="top-bar-toggle" onClick={onToggleRail} aria-label="Toggle navigation">
          <Menu size={16} />
        </button>
        <div>
          <div className="top-bar-title">{title}</div>
          {subtitle && <div className="top-bar-subtitle">{subtitle}</div>}
        </div>
      </div>

      <div className="top-bar-right">
        {trailing}
        {user && !isPremium && (countdown.isTrialing ? (
          <TrialCountdownChip countdown={countdown} />
        ) : (
          <Link to="/premium" className="top-bar-premium-cta" aria-label="Go Premium">
            <F8Sparkle />
            <span>go premium</span>
          </Link>
        ))}
        {user && !isPremium && (
          <div className="top-bar-divider" />
        )}
        <div style={{ position: 'relative' }}>
          <button
            ref={msgBtnRef}
            className={`top-bar-icon-btn ${msgTrayOpen ? 'top-bar-icon-btn--active' : ''}`}
            onClick={() => { setMsgTrayOpen(!msgTrayOpen); setNotifOpen(false); }}
            aria-label="Messages"
          >
            <MessageSquare size={18} />
            {unreadMessages > 0 && (
              <span className="top-bar-badge">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            )}
          </button>
          <MessagesTray open={msgTrayOpen} onClose={() => setMsgTrayOpen(false)} anchorRef={msgBtnRef} />
        </div>
        <div style={{ position: 'relative' }}>
          <button
            ref={bellRef}
            className={`top-bar-icon-btn ${notifOpen ? 'top-bar-icon-btn--active' : ''}`}
            onClick={() => { setNotifOpen(o => !o); setMsgTrayOpen(false); }}
            aria-label="Notifications"
          >
            <Bell size={18} />
            {unreadNotifications > 0 && (
              <span className="top-bar-badge">
                {unreadNotifications > 99 ? '99+' : unreadNotifications}
              </span>
            )}
          </button>
          <NotificationTray
            open={notifOpen}
            onClose={() => setNotifOpen(false)}
            anchorRef={bellRef}
            onUnreadCountChange={setUnreadNotifications}
          />
        </div>
      </div>
    </div>
  );
}
